import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CompanySidebar } from '../components/Dashboard/CompanySidebar';
import { Info } from 'lucide-react';

export const HowMatchingWorksPage: React.FC = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    // Redirect if not authenticated
    useEffect(() => {
        if (!authLoading && !user) {
            navigate('/');
        }
    }, [user, authLoading, navigate]);

    if (!user && !authLoading) {
        return null;
    }

    return (
        <div className="dashboard-layout">
            <CompanySidebar />

            <div className="main-content">
                <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 pb-20">
                    {/* Header */}
                    <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between animate-fade-in gap-4">
                        <div>
                            <h1 className="heading-1 mb-2">How Ranking Works</h1>
                            <p className="text-body text-base lg:text-lg">
                                Understand our advanced semantic matching engine and tiering system.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 animate-fade-in pb-10">
                        {/* Component 1: How Ranking Works (Grey) - Full Width Top or Large Left */}
                        <div className="xl:col-span-12 bg-white border border-slate-100 rounded-2xl p-6 md:p-8 shadow-sm">
                            <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                                <div className="p-3 bg-blue-50 rounded-xl flex-shrink-0">
                                    <Info className="w-6 h-6 md:w-8 md:h-8 text-blue-600" />
                                </div>
                                <div className="space-y-4 max-w-4xl">
                                    <h2 className="heading-2">The Ranking Logic</h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div>
                                            <p className="text-body text-slate-700 leading-relaxed mb-4">
                                                Rankings are calculated using advanced semantic algorithmic matching,
                                                based on how closely each candidate’s experience aligns with the specific job
                                                requirements for the role(s) you are hiring for.
                                            </p>
                                            <p className="text-body text-slate-700 leading-relaxed">
                                                Education and work tiers do not affect the match score. They are used exclusively to
                                                signal the prestige of a candidate’s university and previous employers.
                                            </p>
                                        </div>
                                        <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                                            <h3 className="font-semibold text-slate-900 mb-2">Key Takeaways</h3>
                                            <ul className="space-y-2 text-sm text-slate-600">
                                                <li className="flex items-start gap-2">
                                                    <div className="mt-1.5 w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></div>
                                                    <span>Semantic matching outperforms keyword search</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <div className="mt-1.5 w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></div>
                                                    <span>Contextual understanding of roles & seniority</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Component 2: Semantic Analysis (Grey) - Now Full Width */}
                        <div className="xl:col-span-12 bg-white border border-slate-100 rounded-2xl p-6 md:p-8 shadow-sm flex flex-col">
                            <h3 className="heading-2 mb-4">Semantic Analysis Factors</h3>
                            <div className="space-y-4 flex-1">
                                <p className="text-body text-slate-700 leading-relaxed">
                                    Candidates are matched based on achievement, not just keywords. Our engine compares each candidate's resume against your job description, assessing:
                                </p>
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm md:text-base text-slate-700 font-medium">
                                    <li className="flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100">
                                        <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                                        Relevant skills & technologies
                                    </li>
                                    <li className="flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100">
                                        <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                                        Type of work performed
                                    </li>
                                    <li className="flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100">
                                        <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                                        Role context and seniority
                                    </li>
                                    <li className="flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100">
                                        <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                                        Location and eligibility
                                    </li>
                                </ul>
                                <div className="mt-8 pt-6 border-t border-slate-100">
                                    <p className="text-sm text-slate-600">
                                        Only candidates who meet <strong>core role requirements</strong> are included. Each receives a <strong>Ranking</strong> indicating their fit.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>


    );
};

